import React, { useState, useContext, createContext, useRef, useEffect } from 'react';
import { SlArrowUp } from "react-icons/sl";
import styles from './Accordion.module.css';

const AccordionUpdateContext = createContext();
const ShowHideContext = createContext();

export default function Accordion({children, ...restProps }) {
    const combinedClassName = restProps.className ? `${styles.accordionList} ${restProps.className}` : styles.accordionList;
    return(
        <div className={combinedClassName} {...restProps}>
            {children}
        </div>
    );
} 

Accordion.Item = function AccordionItem({ children, ...restProps }) {
    const [toggleHide, setToggleHide] = useState(false);
    const [updateCounter, setUpdateCounter] = useState(0);
    
    const triggerUpdate = () => {
        setUpdateCounter(prev => prev + 1);
    };

    return (
      <ShowHideContext.Provider value={{ toggleHide, setToggleHide }}>
        <AccordionUpdateContext.Provider value={{ triggerUpdate, updateCounter }}>
          <div className={styles.accordionContainer} {...restProps}>{children}</div>
        </AccordionUpdateContext.Provider>
      </ShowHideContext.Provider>
    );
};

Accordion.Header = function AccordionHeader({ children, noarrow = false, disabled = false, ignoreClasses = [], ...restProps }) {
    const { toggleHide, setToggleHide } = useContext(ShowHideContext);
    const { triggerUpdate } = useContext(AccordionUpdateContext);
    
    const handleClick = (event) => {
        if (disabled) return;
        // Check if the clicked element or its parents have any of the ignored classes
        const shouldIgnore = ignoreClasses.some(className => 
            event.target.closest(`.${className}`) !== null
        );
        
        if (!shouldIgnore) {
            setToggleHide(!toggleHide);
            // Trigger update for all accordions
            triggerUpdate();
        }
    };

    return (
      <div className={styles.accordionHeader} onClick={handleClick} {...restProps}>
        {children}
        {!noarrow && (
          <div className={`${styles.accordionHeaderToggle} ${toggleHide ? '' : styles.accordionHeaderToggleHidden}`}>
            <SlArrowUp />
          </div>
        )}
      </div>
    );
};
  
Accordion.Body = function AccordionBody({ children, startOpen = false, ...restProps }) {
    const { toggleHide, setToggleHide } = useContext(ShowHideContext);
    const { updateCounter } = useContext(AccordionUpdateContext);
    const contentRef = useRef(null);
    const [contentHeight, setContentHeight] = useState(0);

    useEffect(() => {
        if (contentRef.current) {
            setContentHeight(contentRef.current.scrollHeight);
        }

        const resizeObserver = new ResizeObserver(() => {
            if (contentRef.current) {
                setContentHeight(contentRef.current.scrollHeight);
            }
        });

        if (contentRef.current) {
            resizeObserver.observe(contentRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [updateCounter, children]);

    useEffect(() => {
        if (startOpen) {
            setToggleHide(true);
        }
    }, []);

    return (
        <div 
            className={styles.accordionBody}
            style={{
                height: toggleHide ? `${contentHeight}px` : '0',
                maxHeight: toggleHide ? `${contentHeight}px` : '0',
            }}
            {...restProps}
        >
            <div ref={contentRef}>
                {children}
            </div>
        </div>
    );
};
